import { buildApiUrl, isErrorResponse, readJson } from '../api.utils';
import { AuthorizedBlobRequest, AuthorizedJsonRequest, AuthorizedRequest, ErrorResponse } from '../common.types';
import { AiAutoresponderSettings, ChatAiSuggestion, ChatAiSuggestionOptions, ChatCourseAttachmentFile, ChatCourseCatalogCourse, ChatCourseProgramOption, FileDocumentPreview, OperatorChatHistoryEntry, OperatorChatMessage, OperatorChatSummary, UisOverview, WidgetChatSummary, WidgetCourseAttachmentFiles } from './api.types';

interface ReminderPayload {
  remindAt: string;
  comment: string;
}

interface AiAutoresponderSettingsPayload {
  enabled: boolean;
}

interface ChatAiAutoresponderSettingsPayload {
  enabled: boolean;
}

interface ApplyChatPhonePayload {
  phoneNumber: string;
  phoneLabel: string;
}

interface ApplyChatCourseProgramPayload {
  courseProgramId: number;
}

interface GrantChatDemoAccessPayload {
  courseName: string;
}

/** Загружает список операторских чатов. */
export function getOperatorChats(fetchAuthorizedJson: AuthorizedJsonRequest): Promise<OperatorChatSummary[]> {
  return fetchAuthorizedJson<OperatorChatSummary[]>('/api/chats');
}

/** Загружает сообщения конкретного операторского чата. */
export function getOperatorChatMessages(fetchAuthorizedJson: AuthorizedJsonRequest, chatId: number): Promise<OperatorChatMessage[]> {
  return fetchAuthorizedJson<OperatorChatMessage[]>(`/api/chats/${chatId}/messages`);
}

interface PendingAiSuggestion {
  requestId: string;
  status: 'pending';
}

function waitForAiSuggestionPoll(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      reject(new DOMException('Запрос отменён.', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, 1000);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) {
      abort();
    }
  });
}

/** Готовит черновик, ожидая асинхронного задания без длинного HTTP-запроса. */
export async function requestChatAiSuggestion(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  scope: 'chats' | 'wds/chats' = 'chats',
  options: ChatAiSuggestionOptions = { responseMode: 'medium', ignoreAfterLastClient: true },
  signal?: AbortSignal
): Promise<ChatAiSuggestion> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  let timedOut = false;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 135000);
  const basePath = `/api/${scope}/${chatId}/ai-suggestion`;
  let requestId: string | null = null;

  try {
    if (signal?.aborted) {
      controller.abort();
    }
    while (!controller.signal.aborted) {
      const response: Response = await performAuthorizedRequest(
        requestId === null ? basePath : `${basePath}/${encodeURIComponent(requestId)}`,
        requestId === null
          ? { method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(options) }
          : { method: 'GET', signal: controller.signal }
      );
      const payload: ChatAiSuggestion | PendingAiSuggestion | ErrorResponse | null = await readJson<ChatAiSuggestion | PendingAiSuggestion | ErrorResponse>(response);
      if (controller.signal.aborted) {
        break;
      }
      if (!response.ok || !payload || isErrorResponse(payload)) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось подготовить ответ. Попробуйте ещё раз.');
      }
      if (response.status === 202 && 'status' in payload && payload.status === 'pending'
        && typeof payload.requestId === 'string' && payload.requestId.trim()
        && (requestId === null || requestId === payload.requestId)) {
        requestId = payload.requestId;
        await waitForAiSuggestionPoll(controller.signal);
        continue;
      }
      if ('text' in payload && typeof payload.text === 'string' && payload.text.trim()) {
        return { text: payload.text.trim() };
      }
      throw new Error('AI вернул пустой ответ. Попробуйте ещё раз.');
    }
    throw new DOMException('Запрос отменён.', 'AbortError');
  } catch (error) {
    if (timedOut) {
      throw new Error('AI не успел подготовить ответ. Попробуйте ещё раз.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abort);
  }
}

/** Загружает историю действий по конкретному операторскому чату. */
export function getOperatorChatHistory(fetchAuthorizedJson: AuthorizedJsonRequest, chatId: number): Promise<OperatorChatHistoryEntry[]> {
  return fetchAuthorizedJson<OperatorChatHistoryEntry[]>(`/api/chats/${chatId}/history`);
}

/** Загружает глобальную настройку AI-автоответчика. */
export function getAiAutoresponderSettings(fetchAuthorizedJson: AuthorizedJsonRequest): Promise<AiAutoresponderSettings> {
  return fetchAuthorizedJson<AiAutoresponderSettings>('/api/autoresponder/settings');
}

/** Обновляет глобальную настройку AI-автоответчика. */
export function updateAiAutoresponderSettings(
  performAuthorizedRequest: AuthorizedRequest,
  payload: AiAutoresponderSettingsPayload
): Promise<Response> {
  return performAuthorizedRequest('/api/autoresponder/settings', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Обновляет индивидуальную настройку AI-автоответчика для конкретного чата. */
export function updateChatAiAutoresponderSettings(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  payload: ChatAiAutoresponderSettingsPayload
): Promise<Response> {
  return performAuthorizedRequest(`/api/chats/${chatId}/ai-autoresponder`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Загружает список WDS-чатов. */
export function getWidgetChats(fetchAuthorizedJson: AuthorizedJsonRequest): Promise<WidgetChatSummary[]> {
  return fetchAuthorizedJson<WidgetChatSummary[]>('/api/wds/chats');
}

/** Загружает сводку и последние звонки из UIS. */
export function getUisOverview(fetchAuthorizedJson: AuthorizedJsonRequest, limit = 50): Promise<UisOverview> {
  return fetchAuthorizedJson<UisOverview>(`/api/uis/overview?limit=${encodeURIComponent(String(limit))}`);
}

/** Загружает сообщения конкретного WDS-чата. */
export function getWidgetChatMessages(fetchAuthorizedJson: AuthorizedJsonRequest, chatId: number): Promise<OperatorChatMessage[]> {
  return fetchAuthorizedJson<OperatorChatMessage[]>(`/api/wds/chats/${chatId}/messages`);
}

/** Собирает URL для SSE-стрима входящих сообщений. */
export function buildIncomingMessageEventSourceUrl(accessToken: string): string {
  const streamUrl = new URL(buildApiUrl('/api/events'), window.location.origin);
  streamUrl.searchParams.set('accessToken', accessToken);
  return streamUrl.toString();
}

/** Собирает URL содержимого файла чата с access token в query для inline-предпросмотра. */
export function buildChatFileContentUrl(fileId: number, accessToken: string, platform?: 'widget'): string {
  const fileUrl = new URL(buildApiUrl(`/api/${platform === 'widget' ? 'wds' : 'chats'}/files/${fileId}/content`), window.location.origin);
  fileUrl.searchParams.set('accessToken', accessToken);
  return fileUrl.toString();
}

/** Создаёт напоминание внутри выбранного чата. */
export function createChatReminder(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  payload: ReminderPayload,
  scope: 'chats' | 'wds/chats' = 'chats'
): Promise<Response> {
  return performAuthorizedRequest(`/api/${scope}/${chatId}/reminders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Обновляет активное напоминание внутри выбранного чата. */
export function updateChatReminder(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  reminderId: number,
  payload: ReminderPayload,
  scope: 'chats' | 'wds/chats' = 'chats'
): Promise<Response> {
  return performAuthorizedRequest(`/api/${scope}/${chatId}/reminders/${reminderId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Помечает напоминание как отключённое. */
export function dismissChatReminder(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  reminderId: number,
  scope: 'chats' | 'wds/chats' = 'chats'
): Promise<Response> {
  return performAuthorizedRequest(`/api/${scope}/${chatId}/reminders/${reminderId}/dismiss`, {
    method: 'PATCH'
  });
}

/** Загружает содержимое файла из чата. */
export function fetchChatFileBlob(fetchAuthorizedBlob: AuthorizedBlobRequest, fileId: number, platform?: 'widget'): Promise<Blob> {
  return fetchAuthorizedBlob(`/api/${platform === 'widget' ? 'wds' : 'chats'}/files/${fileId}/content`);
}

/** Сохраняет номер телефона в чат и создаёт/привязывает CRM-заявку. */
export function applyChatPhone(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  payload: ApplyChatPhonePayload
): Promise<Response> {
  return performAuthorizedRequest(`/api/chats/${chatId}/contact-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Загружает список доступных курсов для выбора в чате. */
export function getChatCourses(fetchAuthorizedJson: AuthorizedJsonRequest): Promise<ChatCourseCatalogCourse[]> {
  return fetchAuthorizedJson<ChatCourseCatalogCourse[]>('/api/course-catalog/courses');
}

/** Загружает варианты часов для выбранного курса. */
export function getCoursePrograms(fetchAuthorizedJson: AuthorizedJsonRequest, courseId: number): Promise<ChatCourseProgramOption[]> {
  return fetchAuthorizedJson<ChatCourseProgramOption[]>(`/api/course-catalog/courses/${courseId}/programs`);
}

/** Загружает варианты часов для текущего курса внутри выбранного чата. */
export function getChatCoursePrograms(fetchAuthorizedJson: AuthorizedJsonRequest, chatId: number): Promise<ChatCourseProgramOption[]> {
  return fetchAuthorizedJson<ChatCourseProgramOption[]>(`/api/chats/${chatId}/course-programs`);
}

/** Загружает файлы из БД, доступные для курса текущего чата. */
export function getChatCourseAttachmentFiles(fetchAuthorizedJson: AuthorizedJsonRequest, chatId: number): Promise<ChatCourseAttachmentFile[]> {
  return fetchAuthorizedJson<ChatCourseAttachmentFile[]>(`/api/chats/${chatId}/course-files`);
}

/** Загружает содержимое файла курса из БД. */
export function fetchChatCourseAttachmentFileBlob(fetchAuthorizedBlob: AuthorizedBlobRequest, chatId: number, attachmentId: string): Promise<Blob> {
  return fetchAuthorizedBlob(`/api/chats/${chatId}/course-files/${encodeURIComponent(attachmentId)}/content`);
}

export function getWidgetCourseAttachmentFiles(fetchAuthorizedJson: AuthorizedJsonRequest, chatId: number, courseId?: number): Promise<WidgetCourseAttachmentFiles> {
  const query = courseId === undefined ? '' : `?courseId=${courseId}`;
  return fetchAuthorizedJson<WidgetCourseAttachmentFiles>(`/api/wds/chats/${chatId}/course-files${query}`);
}

export function fetchWidgetCourseAttachmentFileBlob(fetchAuthorizedBlob: AuthorizedBlobRequest, chatId: number, attachmentId: string, courseId: number): Promise<Blob> {
  return fetchAuthorizedBlob(`/api/wds/chats/${chatId}/course-files/${encodeURIComponent(attachmentId)}/content?courseId=${courseId}`);
}

/** Сохраняет выбранный курс/часы в чате и, при наличии, обновляет связанную CRM-заявку. */
export function applyChatCourseProgram(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  payload: ApplyChatCourseProgramPayload
): Promise<Response> {
  return performAuthorizedRequest(`/api/chats/${chatId}/course-program`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Выдаёт демо-доступ клиенту по текущему чату и отправляет сообщение с кнопкой входа. */
export function grantChatDemoAccess(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  payload: GrantChatDemoAccessPayload
): Promise<Response> {
  return performAuthorizedRequest(`/api/chats/${chatId}/demo-access`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
}

/** Загружает подготовленный предпросмотр документа. */
export function fetchChatFilePreview(fetchAuthorizedJson: AuthorizedJsonRequest, fileId: number, platform?: 'widget'): Promise<FileDocumentPreview> {
  return fetchAuthorizedJson<FileDocumentPreview>(`/api/${platform === 'widget' ? 'wds' : 'chats'}/files/${fileId}/preview`);
}

/** Отправляет сообщение в WDS-чат. */
export function sendWidgetMessage(performAuthorizedRequest: AuthorizedRequest, chatId: number, text: string, files: ReadonlyArray<File> = []): Promise<Response> {
  return sendChatMessage(performAuthorizedRequest, chatId, text, files, 'wds/chats');
}

/** Отправляет сообщение в основной чат, с файлами или без них. */
export function sendChatMessage(
  performAuthorizedRequest: AuthorizedRequest,
  chatId: number,
  text: string,
  files: ReadonlyArray<File>,
  scope: 'chats' | 'wds/chats' = 'chats'
): Promise<Response> {
  const requestBody = files.length > 0
    ? (() => {
      const formData = new FormData();

      if (text) {
        formData.append('text', text);
      }

      files.forEach((file) => {
        formData.append('files', file, file.name);
      });

      return {
        body: formData
      };
    })()
    : {
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ text })
    };

  return performAuthorizedRequest(`/api/${scope}/${chatId}/messages`, {
    method: 'POST',
    ...requestBody
  });
}

/** Удаляет конкретное сообщение чата. */
export function deleteChatMessage(performAuthorizedRequest: AuthorizedRequest, chatId: number, messageId: number, scope: 'chats' | 'wds/chats' = 'chats'): Promise<Response> {
  return performAuthorizedRequest(`/api/${scope}/${chatId}/messages/${messageId}`, {
    method: 'DELETE'
  });
}

/** Отмечает сообщение операторского чата как прочитанное. */
export function markChatMessageAsRead(performAuthorizedRequest: AuthorizedRequest, chatId: number, messageId: number): Promise<Response> {
  return performAuthorizedRequest(`/api/chats/${chatId}/messages/${messageId}/read`, {
    method: 'PATCH'
  });
}

/** Отмечает сообщение WDS-чата как прочитанное. */
export function markWidgetMessageAsRead(performAuthorizedRequest: AuthorizedRequest, chatId: number, messageId: number): Promise<Response> {
  return performAuthorizedRequest(`/api/wds/chats/${chatId}/messages/${messageId}/read`, {
    method: 'PATCH'
  });
}

/** Создаёт связь между WDS-чатом и клиентским диалогом. */
export function linkWidgetChat(performAuthorizedRequest: AuthorizedRequest, chatId: number, linkedChatId: number): Promise<Response> {
  return performAuthorizedRequest(`/api/wds/chats/${chatId}/links`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ linkedChatId })
  });
}